import { useRef, useState } from "react";

// Approx equirectangular positions (top/left %) + made-up stats — swap in real numbers.
const LOCATIONS = [
  // North America
  { id: "us", city: "New York", country: "United States", flag: "🇺🇸", top: 28, left: 27.5, projects: 34, rank: "#1 overall" },
  { id: "ca", city: "Toronto", country: "Canada", flag: "🇨🇦", top: 23, left: 26.5, projects: 16, rank: "#3 in NA" },
  { id: "mx", city: "Mexico City", country: "Mexico", flag: "🇲🇽", top: 34, left: 32.5, projects: 12, rank: "#5 in NA" },
  { id: "sf", city: "San Francisco", country: "USA", flag: "🇺🇸", top: 27, left: 18.5, projects: 28, rank: "#2 overall" },
  
  // South America
  { id: "br", city: "São Paulo", country: "Brazil", flag: "🇧🇷", top: 52, left: 39.5, projects: 19, rank: "#1 in SA" },
  { id: "ar", city: "Buenos Aires", country: "Argentina", flag: "🇦🇷", top: 60, left: 42.5, projects: 8, rank: "#3 in SA" },
  { id: "co", city: "Bogotá", country: "Colombia", flag: "🇨🇴", top: 46, left: 28.5, projects: 11, rank: "#2 in SA" },
  
  // Europe
  { id: "uk", city: "London", country: "United Kingdom", flag: "🇬🇧", top: 21, left: 49.6, projects: 18, rank: "#2 in EU" },
  { id: "de", city: "Berlin", country: "Germany", flag: "🇩🇪", top: 20.5, left: 53.8, projects: 12, rank: "#4 in EU" },
  { id: "fr", city: "Paris", country: "France", flag: "🇫🇷", top: 23, left: 50.8, projects: 15, rank: "#3 in EU" },
  { id: "it", city: "Rome", country: "Italy", flag: "🇮🇹", top: 27, left: 53.4, projects: 9, rank: "#6 in EU" },
  { id: "es", city: "Madrid", country: "Spain", flag: "🇪🇸", top: 27, left: 47.5, projects: 14, rank: "#5 in EU" },
  { id: "nl", city: "Amsterdam", country: "Netherlands", flag: "🇳🇱", top: 20, left: 51.5, projects: 11, rank: "#7 in EU" },
  { id: "se", city: "Stockholm", country: "Sweden", flag: "🇸🇪", top: 16, left: 55.5, projects: 7, rank: "#8 in EU" },
  
  // Africa
  { id: "za", city: "Cape Town", country: "South Africa", flag: "🇿🇦", top: 66, left: 55.5, projects: 13, rank: "#1 in Africa" },
//   { id: "ke", city: "Nairobi", country: "Kenya", flag: "🇰🇪", top: 44, left: 56.5, projects: 9, rank: "#2 in Africa" },
//   { id: "ng", city: "Lagos", country: "Nigeria", flag: "🇳🇬", top: 35, left: 50.5, projects: 8, rank: "#3 in Africa" },
  
  // Asia
  { id: "kr", city: "Seoul", country: "South Korea", flag: "🇰🇷", top: 29, left: 85.2, projects: 21, rank: "#1 in Asia" },
  { id: "jp", city: "Tokyo", country: "Japan", flag: "🇯🇵", top: 29, left: 88.5, projects: 25, rank: "#2 in Asia" },
  { id: "cn", city: "Shanghai", country: "China", flag: "🇨🇳", top: 32, left: 82.5, projects: 30, rank: "#1 overall" },
//   { id: "in", city: "Mumbai", country: "India", flag: "🇮🇳", top: 38, left: 75.5, projects: 22, rank: "#3 in Asia" },
  { id: "sg", city: "Singapore", country: "Singapore", flag: "🇸🇬", top: 45, left: 83.5, projects: 18, rank: "#4 in Asia" },
  { id: "ae", city: "Dubai", country: "United Arab Emirates", flag: "🇦🇪", top: 36, left: 65.3, projects: 11, rank: "#1 in MENA" },
//   { id: "il", city: "Tel Aviv", country: "Israel", flag: "🇮🇱", top: 31, left: 57.5, projects: 15, rank: "#2 in MENA" },
  { id: "id", city: "Jakarta", country: "Indonesia", flag: "🇮🇩", top: 50, left: 85.5, projects: 14, rank: "#5 in Asia" },
  
  // Oceania
  { id: "au", city: "Sydney", country: "Australia", flag: "🇦🇺", top: 68, left: 88.5, projects: 16, rank: "#1 in Oceania" },
  { id: "nz", city: "Auckland", country: "New Zealand", flag: "🇳🇿", top: 74, left: 93.5, projects: 9, rank: "#2 in Oceania" },
];

export default function GlobalPresenceMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isPanning, setIsPanning] = useState(false);

  // Handle mouse move for parallax effect
  const handleMove = (e) => {
    if (isDragging) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const panX = (0.5 - x) * rect.width * 0.15;
    const panY = (0.5 - y) * rect.height * 0.15;
    if (mapRef.current && !isDragging) {
      mapRef.current.style.transform = `translate(${panX + translate.x}px, ${panY + translate.y}px) scale(${scale})`;
    }
  };

  const handleLeave = () => {
    if (!isDragging) {
      if (mapRef.current) {
        mapRef.current.style.transform = `translate(${translate.x}px, ${translate.y}px) scale(${scale})`;
      }
      setHovered(null);
    }
  };

  // Drag functionality for scrolling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setIsPanning(true);
    setDragStart({
      x: e.clientX - translate.x,
      y: e.clientY - translate.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) {
      handleMove(e);
      return;
    }
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setTranslate({ x: newX, y: newY });
    
    if (mapRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const panX = (0.5 - x) * rect.width * 0.15;
      const panY = (0.5 - y) * rect.height * 0.15;
      mapRef.current.style.transform = `translate(${newX + panX}px, ${newY + panY}px) scale(${scale})`;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setIsPanning(false), 100);
  };

  // Zoom with scroll
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.min(Math.max(scale + delta, 0.8), 2.5);
    setScale(newScale);
    
    if (mapRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const panX = (0.5 - x) * rect.width * 0.15;
      const panY = (0.5 - y) * rect.height * 0.15;
      mapRef.current.style.transform = `translate(${translate.x + panX}px, ${translate.y + panY}px) scale(${newScale})`;
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto font-sans">
      {/* Header */}
      <div className="flex items-baseline justify-between mb-3 px-0.5">
        {/* <div>
          <h3 className="text-base font-semibold text-gray-900">🌍 Global Presence</h3>
          <p className="text-xs text-gray-400 mt-0.5">{LOCATIONS.length} locations across 6 continents</p>
        </div> */}
        {/* <span className="text-[11px] bg-purple-50 text-purple-600 px-2.5 py-1 rounded-full font-medium">
          {LOCATIONS.filter(l => l.rank?.includes("#1")).length} #1 markets
        </span> */}
      </div>

      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        className="relative w-full h-80 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 shadow-lg cursor-grab select-none"
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent pointer-events-none z-5" />
        
        <div
          ref={mapRef}
          className="absolute inset-0"
          style={{ 
            transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transformOrigin: 'center center'
          }}
        >
          {/* World Map Image */}
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/83/Equirectangular_projection_SW.jpg/1280px-Equirectangular_projection_SW.jpg"
            alt="World Map"
            className="w-full h-full object-contain"
            draggable="false"
          />

          {/* Markers with original "K" icon - all pink to purple gradient */}
          {LOCATIONS.map((loc) => {
            return (
              <div
                key={loc.id}
                className="absolute z-10"
                style={{ 
                  top: `${loc.top}%`, 
                  left: `${loc.left}%`, 
                  transform: "translate(-50%, -50%)",
                  pointerEvents: 'auto'
                }}
                onMouseEnter={() => setHovered(loc.id)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="relative flex items-center justify-center w-5 h-5">
                  {/* Pulsing ring - always purple */}
                  <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-60 animate-ping" />
                  
                  {/* Original K marker - always pink to purple gradient */}
                  <span className="relative flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-fuchsia-400 via-purple-500 to-indigo-700 text-white text-[8px] font-bold shadow-sm ring-2 ring-white ring-offset-1 transition-all duration-200 hover:scale-125">
                    K
                  </span>
                </div>

                {/* Tooltip */}
                {hovered === loc.id && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 whitespace-nowrap rounded-xl bg-gray-900/95 backdrop-blur-sm px-3 py-2 text-white shadow-xl pointer-events-none border border-white/10">
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">{loc.flag}</span>
                      <p className="text-[11px] font-semibold leading-tight">
                        {loc.city}, {loc.country}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] text-gray-300">{loc.projects} projects</span>
                      <span className="w-px h-3 bg-gray-600" />
                      <span className={`text-[9px] font-medium ${loc.rank?.includes("#1") ? 'text-amber-300' : 'text-gray-300'}`}>
                        {loc.rank}
                      </span>
                    </div>
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900/95" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Controls hint */}
        <div className="absolute bottom-2 right-2 text-[9px] text-white/70 bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm flex items-center gap-2 pointer-events-none">
          <span>Drag to pan</span>
          <span className="w-px h-3 bg-white/20" />
          <span> Scroll to zoom</span>
        </div>

        {/* Stats overlay */}
        <div className="absolute top-2 left-2 flex gap-2 pointer-events-none">
          {/* <div className="bg-black/20 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <span className="text-[9px] text-white font-medium">📊 {LOCATIONS.reduce((acc, l) => acc + l.projects, 0)} total projects</span>
          </div> */}
        </div>
      </div>

      {/* Legend with flags
      <div className="mt-3 flex flex-wrap gap-1.5 px-0.5">
        {LOCATIONS.map((loc) => (
          <span 
            key={loc.id} 
            className={`text-[9px] ${loc.rank?.includes("#1") ? 'text-amber-600 bg-amber-50' : 'text-gray-500 bg-gray-50'} px-2 py-0.5 rounded-full flex items-center gap-1 border ${loc.rank?.includes("#1") ? 'border-amber-200' : 'border-gray-100'}`}
          >
            <span>{loc.flag}</span>
            {loc.country}
            {loc.rank?.includes("#1") && <span className="text-[8px]">⭐</span>}
          </span>
        ))}
      </div> */}
    </div>
  );
}